/**
 * Production-Grade Authentication Provider
 * 
 * Provides comprehensive authentication state management with:
 * - JWT token handling and refresh
 * - Role-based access control (RBAC)
 * - Multi-tenant support
 * - Session management and security
 * - MFA integration ready
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

// Authentication Types
export interface User {
  id: string;
  email: string;
  name: string;
  roles: Role[];
  tenants: Tenant[];
  currentTenant: string;
  permissions: Permission[];
  mfaEnabled: boolean;
  lastLogin: string;
  sessionId: string;
}

export interface Role {
  id: string;
  name: string;
  permissions: Permission[];
  tenant: string;
  level: number;
}

export interface Permission {
  id: string;
  resource: string;
  action: string;
  conditions?: Record<string, unknown>;
}

export interface Tenant {
  id: string;
  name: string;
  domain: string;
  settings: TenantSettings;
}

export interface TenantSettings {
  mfaRequired: boolean;
  sessionTimeout: number;
  passwordPolicy: PasswordPolicy;
  features: string[];
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxAge: number;
}

export interface JwtPayload {
  sub: string;
  email: string;
  name: string;
  roles: string[];
  tenants: string[];
  currentTenant: string;
  sessionId: string;
  iat: number;
  exp: number;
  mfa_verified: boolean;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokens: {
    access: string | null;
    refresh: string | null;
  };
  error: AuthError | null;
  sessionExpiry: number | null;
  mfaRequired: boolean;
  currentTenant: string | null;
}

export interface AuthError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  recoverable: boolean;
}

// Action Types
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; tokens: { access: string; refresh: string } } }
  | { type: 'LOGIN_FAILURE'; payload: AuthError }
  | { type: 'LOGOUT' }
  | { type: 'TOKEN_REFRESH_SUCCESS'; payload: { access: string } }
  | { type: 'TOKEN_REFRESH_FAILURE'; payload: AuthError }
  | { type: 'MFA_REQUIRED' }
  | { type: 'MFA_SUCCESS'; payload: { access: string } }
  | { type: 'SWITCH_TENANT'; payload: { tenant: string; tokens: { access: string; refresh: string } } }
  | { type: 'UPDATE_PERMISSIONS'; payload: Permission[] }
  | { type: 'SESSION_TIMEOUT' }
  | { type: 'CLEAR_ERROR' };

// Initial State
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  tokens: {
    access: null,
    refresh: null,
  },
  error: null,
  sessionExpiry: null,
  mfaRequired: false,
  currentTenant: null,
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        tokens: action.payload.tokens,
        currentTenant: action.payload.user.currentTenant,
        error: null,
        mfaRequired: false,
      };

    case 'LOGIN_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        tokens: { access: null, refresh: null },
        error: action.payload,
        mfaRequired: action.payload.code === 'MFA_REQUIRED',
      };

    case 'LOGOUT':
      return {
        ...initialState,
      };

    case 'TOKEN_REFRESH_SUCCESS':
      return {
        ...state,
        tokens: {
          ...state.tokens,
          access: action.payload.access,
        },
        error: null,
      };

    case 'TOKEN_REFRESH_FAILURE':
      return {
        ...state,
        tokens: { access: null, refresh: null },
        isAuthenticated: false,
        user: null,
        error: action.payload,
      };

    case 'MFA_REQUIRED':
      return {
        ...state,
        mfaRequired: true,
        isLoading: false,
      };

    case 'MFA_SUCCESS':
      return {
        ...state,
        mfaRequired: false,
        tokens: {
          ...state.tokens,
          access: action.payload.access,
        },
      };

    case 'SWITCH_TENANT':
      return {
        ...state,
        currentTenant: action.payload.tenant,
        tokens: action.payload.tokens,
        user: state.user ? {
          ...state.user,
          currentTenant: action.payload.tenant,
        } : null,
      };

    case 'UPDATE_PERMISSIONS':
      return {
        ...state,
        user: state.user ? {
          ...state.user,
          permissions: action.payload,
        } : null,
      };

    case 'SESSION_TIMEOUT':
      return {
        ...initialState,
        error: {
          code: 'SESSION_TIMEOUT',
          message: 'Your session has expired. Please log in again.',
          recoverable: true,
        },
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

// Context
export interface AuthContextValue extends AuthState {
  login: (email: string, password: string, tenant?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  verifyMfa: (code: string, method: string) => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  hasPermission: (resource: string, action: string, context?: Record<string, unknown>) => boolean;
  hasRole: (roleName: string, tenant?: string) => boolean;
  clearError: () => void;
  updatePermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Storage Keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'ferp_access_token',
  REFRESH_TOKEN: 'ferp_refresh_token',
  USER_DATA: 'ferp_user_data',
  CURRENT_TENANT: 'ferp_current_tenant',
} as const;

// Auth Provider Props
export interface AuthProviderProps {
  children: ReactNode;
  apiBaseUrl: string;
  refreshThreshold?: number; // Minutes before expiry to refresh
  sessionTimeout?: number; // Minutes of inactivity before logout
}

// Auth Provider Component
export function AuthProvider({ 
  children, 
  apiBaseUrl,
  refreshThreshold = 5,
  sessionTimeout = 60 
}: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Token Management
  const getStoredTokens = useCallback(() => {
    const access = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refresh = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    return { access, refresh };
  }, []);

  const storeTokens = useCallback((access: string, refresh: string) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh);
  }, []);

  const clearTokens = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_TENANT);
  }, []);

  // JWT Utilities
  const decodeToken = useCallback((token: string): JwtPayload | null => {
    try {
      return jwtDecode<JwtPayload>(token);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  }, []);

  const isTokenExpired = useCallback((token: string): boolean => {
    const decoded = decodeToken(token);
    if (!decoded) return true;
    
    const now = Date.now() / 1000;
    return decoded.exp < now;
  }, [decodeToken]);

  const shouldRefreshToken = useCallback((token: string): boolean => {
    const decoded = decodeToken(token);
    if (!decoded) return false;
    
    const now = Date.now() / 1000;
    const threshold = refreshThreshold * 60; // Convert to seconds
    return decoded.exp - now < threshold;
  }, [decodeToken, refreshThreshold]);

  // User Creation from JWT
  const createUserFromToken = useCallback((token: string): User | null => {
    const payload = decodeToken(token);
    if (!payload) return null;

    return {
      id: payload.sub,
      email: payload.email,
      name: payload.name,
      roles: [], // Will be populated from separate API call
      tenants: [], // Will be populated from separate API call  
      currentTenant: payload.currentTenant,
      permissions: [],
      mfaEnabled: payload.mfa_verified,
      lastLogin: new Date().toISOString(),
      sessionId: payload.sessionId,
    };
  }, [decodeToken]);

  // API Calls
  const apiCall = useCallback(async <T,>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> => {
    const url = `${apiBaseUrl}${endpoint}`;
    const token = state.tokens.access;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (state.currentTenant) {
      headers['X-Tenant-ID'] = state.currentTenant;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    return response.json();
  }, [apiBaseUrl, state.tokens.access, state.currentTenant]);

  // Authentication Methods
  const login = useCallback(async (email: string, password: string, tenant?: string) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, tenant }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();

      if (data.mfaRequired) {
        dispatch({ type: 'MFA_REQUIRED' });
        return;
      }

      const { access_token, refresh_token } = data;
      const user = createUserFromToken(access_token);

      if (!user) {
        throw new Error('Invalid token received');
      }

      storeTokens(access_token, refresh_token);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          tokens: { access: access_token, refresh: refresh_token },
        },
      });

      // Load full user data asynchronously
      setTimeout(() => updatePermissions(), 100);

    } catch (error) {
      const authError: AuthError = {
        code: 'LOGIN_FAILED',
        message: error instanceof Error ? error.message : 'Login failed',
        recoverable: true,
      };
      dispatch({ type: 'LOGIN_FAILURE', payload: authError });
      throw error;
    }
  }, [apiBaseUrl, createUserFromToken, storeTokens]);

  const logout = useCallback(async () => {
    try {
      if (state.tokens.refresh) {
        await apiCall('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refresh_token: state.tokens.refresh }),
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      clearTokens();
      dispatch({ type: 'LOGOUT' });
    }
  }, [state.tokens.refresh, apiCall, clearTokens]);

  const refreshToken = useCallback(async () => {
    const refresh = state.tokens.refresh;
    if (!refresh) {
      dispatch({ type: 'LOGOUT' });
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refresh }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      const { access_token } = data;

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
      dispatch({ type: 'TOKEN_REFRESH_SUCCESS', payload: { access: access_token } });

    } catch (error) {
      const authError: AuthError = {
        code: 'TOKEN_REFRESH_FAILED',
        message: 'Session expired. Please log in again.',
        recoverable: true,
      };
      clearTokens();
      dispatch({ type: 'TOKEN_REFRESH_FAILURE', payload: authError });
    }
  }, [state.tokens.refresh, apiBaseUrl, clearTokens]);

  const verifyMfa = useCallback(async (code: string, method: string) => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, method }),
      });

      if (!response.ok) {
        throw new Error('MFA verification failed');
      }

      const data = await response.json();
      const { access_token } = data;

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
      dispatch({ type: 'MFA_SUCCESS', payload: { access: access_token } });

    } catch (error) {
      const authError: AuthError = {
        code: 'MFA_FAILED',
        message: 'Multi-factor authentication failed',
        recoverable: true,
      };
      dispatch({ type: 'LOGIN_FAILURE', payload: authError });
      throw error;
    }
  }, [apiBaseUrl]);

  const switchTenant = useCallback(async (tenantId: string) => {
    try {
      const data = await apiCall<{ access_token: string; refresh_token: string }>('/auth/switch-tenant', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId }),
      });

      storeTokens(data.access_token, data.refresh_token);
      localStorage.setItem(STORAGE_KEYS.CURRENT_TENANT, tenantId);

      dispatch({
        type: 'SWITCH_TENANT',
        payload: {
          tenant: tenantId,
          tokens: { access: data.access_token, refresh: data.refresh_token },
        },
      });

    } catch (error) {
      console.error('Tenant switch failed:', error);
      throw error;
    }
  }, [apiCall, storeTokens]);

  const updatePermissions = useCallback(async () => {
    try {
      const data = await apiCall<{ permissions: Permission[] }>('/auth/permissions');
      dispatch({ type: 'UPDATE_PERMISSIONS', payload: data.permissions });
    } catch (error) {
      console.error('Failed to update permissions:', error);
    }
  }, [apiCall]);

  // Permission Checking
  const hasPermission = useCallback((
    resource: string, 
    action: string, 
    context?: Record<string, unknown>
  ): boolean => {
    if (!state.user?.permissions) return false;

    return state.user.permissions.some(permission => {
      if (permission.resource !== resource || permission.action !== action) {
        return false;
      }

      // Check conditions if provided
      if (permission.conditions && context) {
        return Object.entries(permission.conditions).every(([key, value]) => {
          return context[key] === value;
        });
      }

      return true;
    });
  }, [state.user?.permissions]);

  const hasRole = useCallback((roleName: string, tenant?: string): boolean => {
    if (!state.user?.roles) return false;

    return state.user.roles.some(role => {
      if (role.name !== roleName) return false;
      if (tenant && role.tenant !== tenant) return false;
      return true;
    });
  }, [state.user?.roles]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Token Refresh Effect
  useEffect(() => {
    if (!state.tokens.access || !state.isAuthenticated) return undefined;

    const checkToken = () => {
      if (isTokenExpired(state.tokens.access!)) {
        refreshToken();
      } else if (shouldRefreshToken(state.tokens.access!)) {
        refreshToken();
      }
    };

    // Check immediately
    checkToken();

    // Check every minute
    const interval = setInterval(checkToken, 60000);
    return () => clearInterval(interval);
  }, [state.tokens.access, state.isAuthenticated, isTokenExpired, shouldRefreshToken, refreshToken]);

  // Initialize from stored tokens
  useEffect(() => {
    const storedTokens = getStoredTokens();
    if (storedTokens.access && storedTokens.refresh) {
      if (!isTokenExpired(storedTokens.access)) {
        const user = createUserFromToken(storedTokens.access);
        if (user) {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user,
              tokens: { access: storedTokens.access, refresh: storedTokens.refresh },
            },
          });
          updatePermissions();
        }
      } else if (!isTokenExpired(storedTokens.refresh)) {
        refreshToken();
      } else {
        clearTokens();
      }
    }
  }, []); // Run only once on mount

  const contextValue: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshToken,
    verifyMfa,
    switchTenant,
    hasPermission,
    hasRole,
    clearError,
    updatePermissions,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Permission HOC
export interface WithPermissionProps {
  resource: string;
  action: string;
  context?: Record<string, unknown>;
  fallback?: ReactNode;
}

export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  permission: WithPermissionProps
) {
  const ComponentWithPermission = (props: P) => {
    const { hasPermission } = useAuth();
    const hasAccess = hasPermission(permission.resource, permission.action, permission.context);

    if (!hasAccess) {
      return permission.fallback || <div>Access Denied</div>;
    }

    return <WrappedComponent {...props} />;
  };

  ComponentWithPermission.displayName = `withPermission(${WrappedComponent.displayName || WrappedComponent.name})`;
  return ComponentWithPermission;
}

// Role HOC
export interface WithRoleProps {
  role: string;
  tenant?: string;
  fallback?: ReactNode;
}

export function withRole<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  roleConfig: WithRoleProps
) {
  const ComponentWithRole = (props: P) => {
    const { hasRole } = useAuth();
    const hasAccess = hasRole(roleConfig.role, roleConfig.tenant);

    if (!hasAccess) {
      return roleConfig.fallback || <div>Access Denied</div>;
    }

    return <WrappedComponent {...props} />;
  };

  ComponentWithRole.displayName = `withRole(${WrappedComponent.displayName || WrappedComponent.name})`;
  return ComponentWithRole;
}

export default AuthProvider;